import { Replay, SwapHoriz, SwapVert, Undo, ZoomIn, ZoomOut } from '~/ui/assets/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';

import { PdfViewer } from '../PdfViewer/PdfViewer';

import {
  BuiCompare,
  CompareContent,
  ImageControl as StyledImageControl,
  ImageContainer,
  Controllers,
} from './Compare.styled';

export interface CompareProps {
  images?: string[];
  bgColor?: string;
  initialZoom?: number;
}

const ZOOM_STEP = 0.4;
const MAX_ZOOM = 4;
const MIN_ZOOM = 0.8;

interface ImageControlProps {
  image: string;
  count?: number;
  initialZoom?: number;
}

function ImageControl({ image, count, initialZoom }: ImageControlProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const minZoom = useMemo(() => (initialZoom < MIN_ZOOM ? initialZoom : MIN_ZOOM), [initialZoom]);

  const [zoom, setZoom] = useState(initialZoom || minZoom);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0); // degrees
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);

  function onZoomIn(): void {
    setDragging(false);

    if (zoom >= MAX_ZOOM) {
      setZoom(MAX_ZOOM);
      return;
    }

    setZoom((prevZoom) => Math.min(prevZoom + ZOOM_STEP, MAX_ZOOM));
  }

  function onZoomOut(): void {
    setDragging(false);

    if (zoom <= minZoom) {
      setZoom(minZoom);
      return;
    }

    setZoom((prevZoom) => Math.max(prevZoom - ZOOM_STEP, minZoom));
  }

  function handleScrolling(e: React.WheelEvent<HTMLElement>): void {
    if (e.deltaY > 0 && zoom > 0.2) {
      onZoomOut();
    }

    if (e.deltaY < 0) {
      onZoomIn();
    }
  }

  function handleMouseDown(e: React.MouseEvent<HTMLElement>): void {
    setDragging(true);
    const rect = containerRef.current?.getBoundingClientRect();

    if (rect) {
      setOffset({
        x: e.clientX - rect.left - position.x,
        y: e.clientY - rect.top - position.y,
      });
    }
  }

  function handleMouseMove(e: React.MouseEvent<HTMLElement>): void {
    if (!dragging) return;
    const rect = containerRef.current?.getBoundingClientRect();

    const flipX = flipH ? -1 : 1;
    const flipY = flipV ? -1 : 1;

    if (rect) {
      const x = (e.clientX - rect.left - offset.x) * flipX;
      const y = (e.clientY - rect.top - offset.y) * flipY;
      setPosition({ x, y });
    }
  }

  function isPdf(image: string): boolean {
    return image.toLowerCase().startsWith('pdf:');
  }

  function clearPdfUrl(image: string): string {
    return isPdf(image) ? image.replace('pdf:', '') : image;
  }

  function handleMouseUp(): void {
    setDragging(false);
  }

  function rotateLeft(): void {
    setRotation((prev) => prev - 90);
  }

  function rotateRight(): void {
    setRotation((prev) => prev + 90);
  }

  function flipHorizontal(): void {
    setFlipH((prev) => !prev);
  }

  function flipVertical(): void {
    setFlipV((prev) => !prev);
  }

  function reset(): void {
    setZoom(minZoom);
    setPosition({ x: 0, y: 0 });
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (isMouseOver) {
      if (event.key === 'ArrowLeft') {
        rotateLeft();
      } else if (event.key === 'ArrowRight') {
        rotateRight();
      }
    }
  }

  function blockModalScroll(mouseIsOver: boolean): void {
    const modalWrap = document.querySelector('.ant-modal-wrap') as any;
    if (!modalWrap) return;

    if (mouseIsOver) {
      modalWrap.style.overflow = 'hidden';
    } else {
      modalWrap.style.overflow = 'auto';
    }
  }

  useEffect(() => {
    blockModalScroll(isMouseOver);

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMouseOver]);

  return (
    <StyledImageControl onWheel={(e) => handleScrolling(e)} count={count}>
      <ImageContainer
        onMouseLeave={() => {
          handleMouseUp();
          setIsMouseOver(false);
        }}
        onMouseEnter={() => setIsMouseOver(true)}
        onMouseMove={(e) => handleMouseMove(e)}
        onMouseUp={() => handleMouseUp()}
        ref={containerRef}
      >
        {isPdf(image) ? (
          <div
            className="pdf-container"
            draggable={false}
            onMouseDown={handleMouseDown}
            onMouseEnter={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            style={{
              maxHeight: 'calc(100vh - 170px)',
              transform: `scale(${flipH ? -1 : 1}, ${flipV ? -1 : 1}) translate(${position.x}px, ${
                position.y
              }px) rotate(${rotation}deg)`,
              cursor: dragging ? 'grabbing' : 'grab',
              transition: dragging ? 'none' : 'transform 0.2s ease-in-out',
            }}
          >
            <PdfViewer url={clearPdfUrl(image)} scale={zoom} noScroll noPadding showControls={false} />
          </div>
        ) : (
          <img
            alt="compare"
            draggable={false}
            onMouseDown={handleMouseDown}
            onMouseEnter={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            ref={imageRef}
            src={image}
            style={{
              transform: `scale(${zoom * (flipH ? -1 : 1)}, ${zoom * (flipV ? -1 : 1)}) translate(${position.x}px, ${
                position.y
              }px) rotate(${rotation}deg)`,
              cursor: dragging ? 'grabbing' : 'grab',
              transition: dragging ? 'none' : 'transform 0.2s ease-in-out',
            }}
          />
        )}
      </ImageContainer>

      <Controllers className={dragging ? 'dragging' : ''}>
        <button onClick={flipHorizontal} title="Inverter horizontalmente" type="button">
          <SwapHoriz />
        </button>
        <button onClick={rotateLeft} title="Girar para esquerda" type="button">
          <Replay />
        </button>
        <button disabled={zoom >= MAX_ZOOM} onClick={onZoomIn} title="Aumentar zoom" type="button">
          <ZoomIn />
        </button>
        <button onClick={reset} title="Voltar para original" type="button">
          <Undo />
        </button>
        <button disabled={zoom <= minZoom} onClick={onZoomOut} title="Diminuir zoom" type="button">
          <ZoomOut />
        </button>
        <button onClick={rotateRight} title="Girar para direita" type="button">
          <Replay className="invert" />
        </button>
        <button onClick={flipVertical} title="Inverter verticalmente" type="button">
          <SwapVert />
        </button>
      </Controllers>
      <div className="divider" />
    </StyledImageControl>
  );
}

export function Compare({ images = [], bgColor, initialZoom }: CompareProps): ReactElement {
  return (
    <BuiCompare className="bui-compare" bgColor={bgColor}>
      <CompareContent>
        {images.slice(0, 4).map((image, index) => (
          <ImageControl count={images.length} image={image} key={index} initialZoom={initialZoom} />
        ))}
      </CompareContent>
    </BuiCompare>
  );
}
